/**
 * @description Updates the values in a record for a given key
 *
 * @param record - the record which we want to update
 * @example
 * {
 *  '1': { id: 1, title: 'Foo', content: 3 },
 *  '2': { id: 2, title: 'Bar', content: 12 },
 *  '3': { id: 3, title: 'Zee', content: 100 },
 * }
 * @param replacements - the array of items to update the record with
 * @example [{ id: 3, title: 'ZeeUpdated', content: 200 }]
 * @param key - the key with which to determine if the replacement matches a record item
 * @example 'id'
 * @returns The updated record
 * @example
 * {
 *  '1': { id: 1, title: 'Foo', content: 3 },
 *  '2': { id: 2, title: 'Bar', content: 12 },
 *  '3': { id: 3, title: 'ZeeUpdated', content: 200 },
 * }
 */
export function replaceInRecord<T, K extends keyof T>(
    record: Record<string, T>,
    replacements: T[],
    key: K,
): Record<string, T> {
    // Build a lookup map for quick access to replacements by key
    const replacementMap = new Map<T[K], T>(replacements.map(item => [item[key], item]));

    // Create a new record with possible replacements
    const updatedRecord: Record<string, T> = {};

    // Loop over the original record and update the value (if a replacement is present)
    for (const [recordKey, recordValue] of Object.entries(record)) {
        const replacement = replacementMap.get(recordValue[key]);
        updatedRecord[recordKey] = replacement ?? recordValue;
    }

    return updatedRecord;
}
